import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { HiMenu, HiX, HiChevronDown } from "react-icons/hi";
import navLinks from "../data/navLinks";
import siteConfig from "../data/siteConfig";

function DesktopItem({ item }) {
  const [open, setOpen] = useState(false);
  const hasSubmenu = !!item.submenu?.length;

  return (
    <div
      className="relative"
      onMouseEnter={() => hasSubmenu && setOpen(true)}
      onMouseLeave={() => hasSubmenu && setOpen(false)}
    >
      <a
        href={item.href}
        className="flex items-center gap-1 text-sm font-medium text-slate-300 hover:text-white transition-colors py-2"
      >
        {item.label}
        {hasSubmenu && (
          <HiChevronDown
            className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          />
        )}
      </a>

      <AnimatePresence>
        {hasSubmenu && open && (
          <motion.ul
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="glass absolute left-0 top-full mt-1 min-w-[180px] rounded-xl py-2 shadow-card"
          >
            {item.submenu.map((sub) => (
              <li key={sub.label}>
                <a
                  href={sub.href}
                  className="block px-4 py-2 text-sm text-slate-300 hover:text-teal hover:bg-white/5 transition-colors"
                >
                  {sub.label}
                </a>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </div>
  );
}

function MobileItem({ item, onNavigate }) {
  const [open, setOpen] = useState(false);
  const hasSubmenu = !!item.submenu?.length;

  return (
    <li className="border-b border-white/5">
      <div
        className="flex items-center justify-between py-3 cursor-pointer"
        onClick={() => (hasSubmenu ? setOpen((o) => !o) : onNavigate())}
      >
        <a href={item.href} className="text-slate-200 font-medium" onClick={(e) => hasSubmenu && e.preventDefault()}>
          {item.label}
        </a>
        {hasSubmenu && (
          <HiChevronDown className={`transition-transform ${open ? "rotate-180" : ""}`} />
        )}
      </div>
      <AnimatePresence>
        {hasSubmenu && open && (
          <motion.ul
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden pl-4 pb-2"
          >
            {item.submenu.map((sub) => (
              <li key={sub.label}>
                <a
                  href={sub.href}
                  onClick={onNavigate}
                  className="block py-2 text-sm text-slate-400 hover:text-teal"
                >
                  {sub.label}
                </a>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </li>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass shadow-card py-3" : "bg-transparent py-5"
      }`}
    >
      <nav className="flex items-center justify-between px-6 md:px-12 lg:px-24">
        {/* Logo -- swap for an <img> tag if you have a logo file */}
        <a href="#home" className="flex items-center gap-2 group">
          <img
            src="/media/Logo.png"
            alt="Winrevo"
            className="h-14"
          />
        </a>

        {/* Desktop nav */}
        <ul className="hidden md:flex items-center gap-8">
          {navLinks.map((item) => (
            <li key={item.label}>
              <DesktopItem item={item} />
            </li>
          ))}
        </ul>

        <a href="#cta" className="hidden md:inline-flex btn-primary !py-2.5 !px-5 text-sm">
          Hubungi Kami
        </a>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-white text-2xl"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {mobileOpen ? <HiX /> : <HiMenu />}
        </button>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden glass mx-4 mt-3 rounded-2xl px-5 overflow-hidden"
          >
            <ul>
              {navLinks.map((item) => (
                <MobileItem key={item.label} item={item} onNavigate={() => setMobileOpen(false)} />
              ))}
            </ul>
            <a
              href="#cta"
              onClick={() => setMobileOpen(false)}
              className="btn-primary w-full justify-center my-4"
            >
              Hubungi Kami
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
